/**
 * Thin client for the `tavus` edge function. Field names mirror the Tavus
 * REST API 1:1 (`pal_id`, `face_id`, `voice_id`, `system_prompt`) so
 * nothing has to be translated at the boundary.
 */
import { createLogger } from "@/lib/frontend-logger";
import { CORRELATION_HEADER, nextCorrelationId } from "@/lib/correlation";
import { appendOp } from "@/lib/opSlug";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const ENDPOINT = `${SUPABASE_URL}/functions/v1/tavus`;

const logger = createLogger("tavus.client");

async function call<T>(action: string, params: Record<string, unknown> = {}): Promise<T> {
  const correlationId = nextCorrelationId();
  const startedAt = performance.now();
  const res = await fetch(appendOp(ENDPOINT, `tavus-${action.replace(/_/g, "-")}`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_KEY,
      authorization: `Bearer ${SUPABASE_KEY}`,
      [CORRELATION_HEADER]: correlationId,
    },
    body: JSON.stringify({ action, ...params }),
  });
  const durationMs = Math.round(performance.now() - startedAt);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = (data && (data.error || data.message)) || `tavus ${action} failed (${res.status})`;
    logger.error("tavus call failed", { action, status: res.status, correlationId, durationMs, error: msg });
    throw new Error(msg);
  }
  logger.info("tavus call ok", { action, status: res.status, correlationId, durationMs });
  return data as T;
}

export type TavusFace = {
  face_id?: string;
  replica_id?: string;
  face_name?: string;
  replica_name?: string;
  name?: string;
  thumbnail_url?: string;
  thumbnail_image_url?: string;
  thumbnail_video_url?: string;
  thumbnail?: string | { url?: string; image_url?: string };
  gif_url?: string;
  face_type?: string;
  replica_type?: string;
  [k: string]: unknown;
};

export type TavusVoice = {
  /**
   * Tavus /v2/voices identifies rows by `voice_name` + `face_id`. There
   * is no numeric/uuid id in the payload; treat `voice_name` as the id
   * string we forward to `layers.tts.voice_name` on the pal.
   */
  voice_name?: string;
  face_id?: string;
  /** e.g. `cartesia`, `elevenlabs`, `playht`. Required alongside voice_name. */
  tts_engine?: string;
  audio_url?: string;
  tags?: Array<{ tag_name?: string }>;
  // Kept optional in case a future API version adds a stable name field.
  name?: string;
  gender?: string;
  language?: string;
  [k: string]: unknown;
};

export type ElevenLabsVoice = {
  voice_id: string;
  name?: string;
  category?: string;
  labels?: Record<string, string>;
  preview_url?: string;
  description?: string;
};

export const tavus = {
  listFaces: () => call<{ faces: TavusFace[] }>("list_faces"),
  listVoices: () => call<{ voices: TavusVoice[] }>("list_voices"),
  listElevenLabsVoices: () =>
    call<{ voices: ElevenLabsVoice[] }>("elevenlabs_list_voices"),
  createPal: (input: {
    system_prompt: string;
    face_id: string;
    elevenlabs_voice_id?: string;
    pal_name?: string;
    /** Knowledge-base documents available to the pal in every conversation. */
    document_ids?: string[];
  }) => call<{ pal_id: string; pal: unknown }>("create_pal", input),
  updatePal: (input: {
    pal_id: string;
    system_prompt?: string;
    face_id?: string;
    /**
     * ElevenLabs voice override on the pal's TTS layer.
     * - `undefined` (key omitted) → don't touch voice at all
     * - `""` or `null` → clear any existing voice override
     * - non-empty string → set/replace with an ElevenLabs voice
     */
    elevenlabs_voice_id?: string | null;
    pal_name?: string;
    /**
     * Knowledge-base documents. Omit the key to leave them untouched, pass
     * `[]` to detach everything.
     */
    document_ids?: string[];
  }) => call<{ pal: unknown }>("update_pal", input),
  deletePal: (pal_id: string) => call<{ ok: true }>("delete_pal", { pal_id }),

  // ── Knowledge base documents ───────────────────────────────
  createDocument: (input: {
    document_url: string;
    document_name?: string;
    tags?: string[];
  }) =>
    call<{
      document_id: string;
      document_name?: string;
      document_url?: string;
      status?: string;
      error_message?: string | null;
    }>("create_document", input),
  getDocument: (document_id: string) =>
    call<{
      document_id: string;
      document_name?: string;
      document_url?: string;
      status?: string;
      progress?: number | null;
      error_message?: string | null;
      /** True when Tavus no longer has this document (404 upstream). */
      missing?: boolean;
    }>("get_document", { document_id }),
  deleteDocument: (document_id: string) =>
    call<{ ok: true }>("delete_document", { document_id }),

  // ── PAL skills ─────────────────────────────────────────────
  attachSkill: (input: {
    pal_id: string;
    skill_id: string;
    config?: Record<string, unknown>;
  }) => call<{ skill: unknown }>("attach_skill", input),
  detachSkill: (input: { pal_id: string; skill_id: string }) =>
    call<{ ok: true }>("detach_skill", input),
  /** Actual attachment state of a skill on the pal (404 → `attached: false`). */
  getPalSkill: (input: { pal_id: string; skill_id: string }) =>
    call<{ attached: boolean; config: Record<string, unknown> }>(
      "get_pal_skill",
      input,
    ),

  createConversation: (input: {
    pal_id: string;
    face_id: string;
    custom_greeting?: string;
    conversational_context?: string;
    conversation_name?: string;
    language?: string;
  }) =>
    call<{
      conversation_id: string;
      conversation_url: string;
      status?: string;
      conversation: unknown;
    }>("create_conversation", input),
  endConversation: (conversation_id: string) =>
    call<{ ok: true }>("end_conversation", { conversation_id }),
  getConversation: (conversation_id: string) =>
    call<{
      conversation_id: string;
      status?: string;
      started_at: string | null;
      ended_at: string | null;
      duration: number | null;
      raw?: unknown;
    }>("get_conversation", { conversation_id }),
  getConversationTranscript: (conversation_id: string) =>
    call<{
      session_active?: boolean;
      next_timestamp?: number;
      transcript_data: Array<{
        role: "user" | "avatar";
        transcript: string;
        absolute_timestamp: number;
        relative_timestamp: number;
      }>;
      raw?: unknown;
    }>("get_conversation_transcript", { conversation_id }),
};